import type { InternalAxiosRequestConfig, AxiosResponse, AxiosError } from 'axios'
import axios from 'axios'
import {
  getAccessToken,
  getRefreshToken,
  saveAuthTokens,
  clearAuthTokens,
} from '../utils/tokenStorage'
import { useAuthStore } from '../../features/auth/hooks/useAuthStore'
import { getApiBaseUrl } from './apiClient'
import { getActiveAcademicYearId } from './academicYear'
import { showToastMessage } from '../../components/Toast'
import { resolveBranchId } from '../utils/branchUtils'

export const DEFAULT_ACADEMIC_YEAR_ID = ''
let isRefreshing = false
let failedQueue: Array<{
  resolve: (token: string) => void
  reject: (err: unknown) => void
}> = []

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach((promise) => {
    if (error) {
      promise.reject(error)
    } else if (token) {
      promise.resolve(token)
    }
  })
  failedQueue = []
}

export const resetRefreshState = () => {
  isRefreshing = false
  processQueue(new Error('Auth session terminated'), null)
  failedQueue = []
}

export const requestInterceptor = (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
  const apiBaseUrl = getApiBaseUrl()
  if (!config.baseURL) {
    config.baseURL = apiBaseUrl
  }

  if (config.headers) {
    config.headers['ngrok-skip-browser-warning'] = 'true'
  }

  if (config.headers && config.data instanceof FormData) {
    if (typeof config.headers.delete === 'function') {
      config.headers.delete('Content-Type')
      config.headers.delete('content-type')
    } else {
      delete config.headers['Content-Type']
      delete config.headers['content-type']
    }
  }

  const token =
    useAuthStore.getState().accessToken ||
    getAccessToken() ||
    localStorage.getItem('auth_token') ||
    localStorage.getItem('access_token')

  const isAbsoluteUrl = Boolean(config.url?.startsWith('http://') || config.url?.startsWith('https://'))
  const isExternal =
    isAbsoluteUrl &&
    (!apiBaseUrl || !config.url?.startsWith(apiBaseUrl))

  if (config.url && !isAbsoluteUrl && !config.url.startsWith('/api') && !config.url.startsWith('api/')) {
    config.url = `/api${config.url.startsWith('/') ? '' : '/'}${config.url}`
  }

  const isAcademicSessionsRequest = config.url?.includes('/api/academic-sessions')

  const isRefreshEndpoint =
    config.url?.includes('/api/auth/refresh') ||
    config.url?.includes('/api/auth/refresh-token')

  if (config.headers && !isExternal) {
    if (token && !isRefreshEndpoint && !config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`
    }

    const existingBranchId =
      (config.headers['x-branch-id'] as string | undefined) ||
      (config.headers['X-Branch-Id'] as string | undefined)

    let branchId = resolveBranchId(existingBranchId) || ''

    let orgId =
      localStorage.getItem('active_org_id') ||
      localStorage.getItem('org_id') ||
      localStorage.getItem('x_org_id') ||
      localStorage.getItem('orgId') ||
      localStorage.getItem('activeOrgId') ||
      ''

    if (!branchId || !orgId) {
      try {
        const storedUser = JSON.parse(localStorage.getItem('school-erp-user') || '{}')
        if (!branchId && storedUser?.branchId) {
          branchId = storedUser.branchId
        }
        if (!orgId && storedUser?.orgId) {
          orgId = storedUser.orgId
        }
      } catch {
        // ignore
      }
    }

    if (!orgId && token) {
      try {
        const parts = token.split('.')
        if (parts.length === 3) {
          const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')))
          if (payload?.orgId) {
            orgId = payload.orgId
          }
          if (!branchId && Array.isArray(payload?.branchIds) && payload.branchIds.length > 0) {
            branchId = payload.branchIds[0]
          }
        }
      } catch {
        // ignore
      }
    }

    const hasExplicitNoBranchHeader =
      config.headers['x-branch-id'] === null ||
      config.headers['x-branch-id'] === false ||
      config.headers['x-branch-id'] === 'none' ||
      config.headers['X-Branch-Id'] === null ||
      config.headers['X-Branch-Id'] === false ||
      config.headers['X-Branch-Id'] === 'none'

    if (hasExplicitNoBranchHeader) {
      if (typeof config.headers.delete === 'function') {
        config.headers.delete('x-branch-id')
        config.headers.delete('X-Branch-Id')
        config.headers.delete('x-org-id')
        config.headers.delete('X-Org-Id')
      } else {
        delete config.headers['x-branch-id']
        delete config.headers['X-Branch-Id']
        delete config.headers['x-org-id']
        delete config.headers['X-Org-Id']
      }
    } else {
      if (branchId) {
        config.headers['x-branch-id'] = branchId
        config.headers['X-Branch-Id'] = branchId
      }

      if (orgId) {
        config.headers['x-org-id'] = orgId
        config.headers['X-Org-Id'] = orgId
      }
    }

    const existingAcademicYear =
      (config.headers['x-academic-year'] as string | undefined) ||
      (config.headers['X-Academic-Year'] as string | undefined)

    const storedAcademicYear =
      localStorage.getItem('x_academic_year') ||
      localStorage.getItem('academic_year_id') ||
      localStorage.getItem('academic_session_id')

    const isValidHeader = (val?: string) =>
      Boolean(
        val &&
        val !== 'REPLACE_WITH_REAL_ACADEMIC_YEAR_UUID'
      )

    const academicYear =
      storedAcademicYear ||
      (isValidHeader(existingAcademicYear) ? existingAcademicYear : undefined) ||
      DEFAULT_ACADEMIC_YEAR_ID

    if (!isAcademicSessionsRequest && academicYear && academicYear !== 'REPLACE_WITH_REAL_ACADEMIC_YEAR_UUID') {
      config.headers['X-Academic-Year'] = academicYear
      config.headers['x-academic-year'] = academicYear
      if (!config.headers['x-session-id'] && !config.headers['X-Session-Id']) {
        config.headers['x-session-id'] = academicYear
        config.headers['X-Session-Id'] = academicYear
      }
      // x-session-id carries the same UUID as x-academic-year — required by
      // endpoints such as GET /api/timetables/teacher-schedule
      config.headers['x-session-id'] = academicYear
    }

    if (
      config.url?.includes('/api/grades/grades') ||
      config.url?.includes('/api/timetables') ||
      config.url?.includes('/api/subjects')
    ) {
      console.log('[ACADEMIC YEAR REQUEST HEADER]', {
        url: config.url,
        sent_x_academic_year: config.headers['x-academic-year'],
        localStorage_academic_year_id: typeof localStorage !== 'undefined' ? localStorage.getItem('academic_year_id') : undefined,
      })
    }
  }

  return config
}

export const responseInterceptor = (response: AxiosResponse): AxiosResponse => {
  return response
}

export const errorInterceptor = async (error: AxiosError): Promise<any> => {
  const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean }

  // Clear stale academic year storage if backend reports ACADEMIC_YEAR_NOT_FOUND, ACADEMIC_YEAR_INACTIVE, or ACADEMIC_YEAR_INVALID
  const errData = error.response?.data as any
  const errStatus = error.response?.status
  const responseDataStr = JSON.stringify(errData || '').toLowerCase()
  const isAcademicYearError =
    (errStatus === 400 || errStatus === 404 || errStatus === 409 || errStatus === 422) &&
    (errData?.error === 'ACADEMIC_YEAR_NOT_FOUND' ||
      errData?.error === 'ACADEMIC_YEAR_INACTIVE' ||
      errData?.error === 'ACADEMIC_YEAR_INVALID' ||
      errData?.message === 'ACADEMIC_YEAR_NOT_FOUND' ||
      errData?.message === 'ACADEMIC_YEAR_INACTIVE' ||
      errData?.message === 'ACADEMIC_YEAR_INVALID' ||
      responseDataStr.includes('academic_year_not_found') ||
      responseDataStr.includes('academic_year_inactive') ||
      responseDataStr.includes('academic_year_invalid') ||
      responseDataStr.includes('academic session') ||
      responseDataStr.includes('inactive academic year'))

  if (isAcademicYearError) {
    console.warn('[Interceptors] Academic year rejected by backend. Evicting stale academic year storage:', {
      status: errStatus,
      error: errData?.error || errData?.message,
    })
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem('academic_year_id')
      localStorage.removeItem('academic_session_id')
      localStorage.removeItem('x_academic_year')
      localStorage.removeItem('managed_academic_year')
    }
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem('academic_year_id')
      sessionStorage.removeItem('academic_session_id')
      sessionStorage.removeItem('x_academic_year')
    }

    if (originalRequest && !originalRequest._retry) {
      originalRequest._retry = true
      try {
        const freshAcademicYearId = await getActiveAcademicYearId()
        if (freshAcademicYearId && originalRequest.headers) {
          originalRequest.headers['X-Academic-Year'] = freshAcademicYearId
          originalRequest.headers['x-academic-year'] = freshAcademicYearId
          console.info('[Interceptors] Retrying request with fresh active academic year:', freshAcademicYearId)
          return axios.request(originalRequest)
        }
      } catch (retryErr) {
        console.error('[Interceptors] Failed to resolve fresh academic year for retry:', retryErr)
      }
    }
  }

  const status = error.response?.status
  const errDataStr = JSON.stringify(error.response?.data || {}).toLowerCase()

  const isTokenError =
    status === 401 ||
    errDataStr.includes('invalid_or_expired_token') ||
    errDataStr.includes('expired_token') ||
    errDataStr.includes('invalid_token') ||
    errDataStr.includes('jwt expired') ||
    errDataStr.includes('token_expired')

  if (isTokenError) {
    console.warn('[API Client] Invalid or expired token error detected for:', originalRequest?.url, 'Message:', errDataStr)
  }

  const isAuthEndpoint =
    originalRequest?.url?.includes('/api/auth/login') ||
    originalRequest?.url?.includes('/api/auth/refresh') ||
    originalRequest?.url?.includes('/api/auth/logout') ||
    originalRequest?.url?.includes('/api/auth/tenant/login') ||
    originalRequest?.url?.includes('/api/auth/superadmin/login')

  if (isTokenError && originalRequest) {
    if (isAuthEndpoint) {
      if (
        originalRequest.url?.includes('/api/auth/refresh') ||
        originalRequest.url?.includes('/api/auth/refresh-token')
      ) {
        console.warn('[API Client] Refresh token endpoint returned 401. Session expired. Evicting session.')
        clearAuthTokens()
        useAuthStore.getState().logout()
        if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
          window.location.href = '/login'
        }
      }
      return Promise.reject(error)
    }

    if (!originalRequest._retry) {
      const refreshToken = getRefreshToken() || localStorage.getItem('refresh_token') || ''
      if (!refreshToken) {
        console.warn('[API Client] No refresh token available on 401. Evicting session and redirecting to login.')
        clearAuthTokens()
        useAuthStore.getState().logout()
        if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
          window.location.href = '/login'
        }
        return Promise.reject(error)
      }

      originalRequest._retry = true
      const currentBaseUrl = getApiBaseUrl()

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({
            resolve: (token: string) => {
              if (originalRequest.headers) {
                originalRequest.headers.Authorization = `Bearer ${token}`
              }
              if (originalRequest.baseURL === undefined) {
                originalRequest.baseURL = currentBaseUrl
              }
              resolve(axios.request(originalRequest))
            },
            reject: (err) => {
              reject(err)
            },
          })
        }) as any
      }

      isRefreshing = true

      try {
        console.info('[API Client] Rotating token via POST /api/auth/refresh...')
        let response
        const storedAcademicYear =
          (typeof localStorage !== 'undefined'
            ? localStorage.getItem('academic_year_id') ||
              localStorage.getItem('academic_session_id') ||
              localStorage.getItem('x_academic_year')
            : undefined) || undefined

        try {
          // Attempt 1: Standard POST /api/auth/refresh with body & headers (un-authenticated by old access token)
          response = await axios.post(
            `${currentBaseUrl}/api/auth/refresh`,
            refreshToken ? { refreshToken, refresh_token: refreshToken } : {},
            {
              withCredentials: true,
              headers: {
                'Content-Type': 'application/json',
                Authorization: '', // Explicitly strip expired access token
                ...(refreshToken
                  ? { 'x-refresh-token': refreshToken, 'X-Refresh-Token': refreshToken }
                  : {}),
                ...(storedAcademicYear
                  ? {
                      'X-Academic-Year': storedAcademicYear,
                      'x-academic-year': storedAcademicYear,
                    }
                  : {}),
              },
            }
          )
        } catch (firstErr) {
          console.warn('[API Client] Primary /api/auth/refresh (unauthenticated) failed, retrying with Bearer refreshToken header:', firstErr)
          try {
            // Attempt 2: Retry with Authorization: Bearer <refreshToken>
            response = await axios.post(
              `${currentBaseUrl}/api/auth/refresh`,
              refreshToken ? { refreshToken, refresh_token: refreshToken } : {},
              {
                withCredentials: true,
                headers: {
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${refreshToken}`,
                  ...(refreshToken
                    ? { 'x-refresh-token': refreshToken, 'X-Refresh-Token': refreshToken }
                    : {}),
                },
              }
            )
          } catch (secondErr) {
            console.warn('[API Client] Retrying fallback endpoint /api/auth/refresh-token:', secondErr)
            // Attempt 3: Retry fallback endpoint /api/auth/refresh-token
            response = await axios.post(
              `${currentBaseUrl}/api/auth/refresh-token`,
              refreshToken ? { refreshToken, refresh_token: refreshToken } : {},
              {
                withCredentials: true,
                headers: {
                  'Content-Type': 'application/json',
                  Authorization: '',
                  ...(refreshToken
                    ? { 'x-refresh-token': refreshToken, 'X-Refresh-Token': refreshToken }
                    : {}),
                },
              }
            )
          }
        }

        const newAccessToken =
          response.data?.accessToken ||
          response.data?.token ||
          response.data?.data?.accessToken ||
          response.data?.data?.token

        const newRefreshToken =
          response.data?.refreshToken ||
          response.data?.token ||
          response.data?.data?.refreshToken

        if (newAccessToken) {
          console.info('[API Client] Token refresh successful. Updating stored auth credentials.')
          saveAuthTokens(newAccessToken, newRefreshToken || refreshToken)
          useAuthStore.getState().setAccessToken(newAccessToken)

          axios.defaults.headers.common['Authorization'] = `Bearer ${newAccessToken}`
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${newAccessToken}`
          }
          if (originalRequest.baseURL === undefined) {
            originalRequest.baseURL = currentBaseUrl
          }

          processQueue(null, newAccessToken)
          return axios.request(originalRequest) as any
        } else {
          throw new Error('No access token returned in refresh response')
        }
      } catch (refreshErr) {
        console.error('[API Client] Refresh token process failed. Session expired:', refreshErr)
        clearAuthTokens()
        useAuthStore.getState().logout()
        processQueue(refreshErr, null)
        showToastMessage('Session expired. Please log in again.', 'error')
        if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
          window.location.href = '/login'
        }
        return Promise.reject(refreshErr)
      } finally {
        isRefreshing = false
      }
    }
  }

  return Promise.reject(error)
}
